"use client";

// import { useState } from "react";
import { migrationCreate } from "../services/migration/migrationCreate";
// import { isYamlFile, isZipFile, yamlToDataUri, zipToDataUri } from "../utils/format-utils";
// import type { MigrationFormData, MigrateSpecificationHeaders, MigrationResponse } from "../types/migrations";
// import { extractApiErrorMessage } from "../utils/error-handling";
import { CreateMigrateParams } from "../services/migration/types";
import { useState } from "react";
import { migrateSpecification } from "../services/migration/migrateSpecification";
import { isYamlFile, isZipFile, yamlToDataUri, zipToDataUri } from "../utils/format-utils";

import type {
  MigrationFormData,
  MigrateSpecificationParams,
  MigrateSpecificationHeaders,
  MigrationResponse,
} from "../types/migrations";
import { extractApiErrorMessage } from "../utils/error-handling";

interface UseMigrationProcessProps {
  accountId: string;
}

interface UseMigrationProcessResult {
  isLoading: boolean;
  activeStep: number;
  stepperState: "default" | "loading" | "success";
  migrationResult: MigrationResponse | null;
  toastMessage: string;
  showToast: boolean;
  setShowToast: (value: boolean) => void;
  startMigration: (formData: MigrationFormData, file: File) => Promise<boolean>;
  setActiveStep: (step: number) => void;
  setStepperState: (state: "default" | "loading" | "success") => void;
  setMigrationResult: any;
}

/**
 * Custom hook for handling the migration process
 */
export function useMigrationProcess({ accountId }: UseMigrationProcessProps): UseMigrationProcessResult {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [stepperState, setStepperState] = useState<"default" | "loading" | "success">("default");
  const [migrationResult, setMigrationResult] = useState<MigrationResponse | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  // setIsLoading(true);
  const startMigration = async (formData: MigrationFormData, file: File): Promise<boolean> => {
    if (!accountId) {
      setToastMessage("Account ID not found. Please log in again.");
      setShowToast(true);
      return false;
    }

    // setActiveStep(1); // Go to processing step
    setStepperState("loading");

    try {
      // setIsLoading(true);
      let dataUri: string;

      // Detect file type and convert to data URI
      if (isYamlFile(file)) {
        const fileContent = await file.text();
        dataUri = yamlToDataUri(fileContent);
      } else if (isZipFile(file)) {
        dataUri = await zipToDataUri(file);
      } else {
        // If not YAML or ZIP, show error message
        setIsLoading(false);
        setStepperState("default");
        setToastMessage("Unsupported file format. Please upload a YAML or ZIP file.");
        setShowToast(true);
        setActiveStep(0);
        return false;
      }
      // Prepare parameters according to API structure
      const params: CreateMigrateParams = {
        content: dataUri,
        migrationName: formData.projectName,
        originSpecType: formData.sourceSystem === "ibm" ? "IBM_APIC" : "WSDL",
        destinationSpecType: formData.destinationSystem === "apigee" ? "APIGEE" : "WSDL",
        prompt: "Convert this YAML file to Apigee xml format.",
        uploadToSource: true,
        sourceFileName: file.name,
      };

      // Use accountId from hook
      const headers: MigrateSpecificationHeaders = {
        accountId: accountId,
        responseFormat: "zip",
        aiModel: formData.llmModel === "gemini" ? "gemini" : "openai",
      };
      setStepperState("loading");
      const response = await migrationCreate(params, headers);

      if (response.success) {
        setIsLoading(false);
        setMigrationResult(response);
        setStepperState("success");
        setActiveStep(1); // Go to results step
        return true;
      } else {
        setToastMessage(response.error || "An error occurred during migration");
        setShowToast(true);
        setActiveStep(0);
        setStepperState("default");
        return false;
      }
    } catch (error) {
      let errorMessage = extractApiErrorMessage(error);

      if (error instanceof TypeError && error.message.includes("network")) {
        errorMessage = "Network error. Please check your internet connection.";
      }

      setIsLoading(false);
      setStepperState("default");
      setToastMessage(errorMessage);
      setShowToast(true);
      setActiveStep(0);
      return false;
    }
  };

  return {
    isLoading,
    activeStep,
    stepperState,
    migrationResult,
    toastMessage,
    showToast,
    setShowToast,
    startMigration,
    setActiveStep,
    setStepperState,
    setMigrationResult,
  };
}
